import { useMemo, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Pencil, Plus, Search, Trash2, Undo2, X } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { ScrollPage } from '@/components/ScrollPage';
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog';
import { BusinessUnitDialog } from '@/components/business-units/BusinessUnitDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useBusinessUnits, useDeleteBusinessUnit, useRestoreBusinessUnit } from '@/hooks/useBusinessUnits';
import { useContracts } from '@/hooks/useContracts';
import { useAuth } from '@/contexts/AuthContext';
import { formatCalendarDate } from '@/lib/dateFormat';
import { formatNumber } from '@/lib/numberFormat';
import { cn } from '@/lib/utils';
import type { VolumeDistribution } from '@/types';
import type { Contract } from '@/services/contractService';
import type { BusinessUnit } from '@/services/businessUnitService';

function RowAction({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** Tons a contract allocates to one business unit */
function allocatedTons(contract: Contract, buName: string) {
  if (contract.businessUnitMode === 'Single') return contract.totalVolume / 1000;
  const vd = contract.volumeDistributions?.find((d: VolumeDistribution) => d.businessUnitName === buName);
  return (vd?.volumeKgs || 0) / 1000;
}

function usesBusinessUnit(contract: Contract, buName: string) {
  return contract.businessUnitMode === 'Single'
    ? contract.businessUnitName === buName
    : !!contract.volumeDistributions?.some((d: VolumeDistribution) => d.businessUnitName === buName);
}

export default function BusinessUnits() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [expandedBuId, setExpandedBuId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [dialog, setDialog] = useState<{ open: boolean; businessUnit: BusinessUnit | null }>({ open: false, businessUnit: null });
  const [pendingAction, setPendingAction] = useState<{ type: 'delete' | 'restore'; bu: BusinessUnit } | null>(null);

  const deleteBusinessUnit = useDeleteBusinessUnit();
  const restoreBusinessUnit = useRestoreBusinessUnit();

  const { data: businessUnits, isLoading: isLoadingBUs, error } = useBusinessUnits(isAdmin && showDeleted);
  const { data: contractsData, isLoading: isLoadingContracts } = useContracts({ pageSize: 1000 });

  const rows = useMemo(() => {
    const contracts: Contract[] = contractsData?.items ?? [];
    const withTotals = (businessUnits ?? []).map((bu) => {
      const buContracts = contracts.filter((c) => usesBusinessUnit(c, bu.name));
      const totalVolume = buContracts.reduce((sum, c) => sum + allocatedTons(c, bu.name), 0);
      return { ...bu, contracts: buContracts, totalVolume };
    });
    const grandTotal = withTotals.reduce((sum, bu) => sum + bu.totalVolume, 0);
    const query = search.trim().toLowerCase();
    return withTotals
      .filter((bu) => !query || [bu.name, bu.code, bu.location, bu.businessUnitKey].some((v) => v?.toLowerCase().includes(query)))
      .sort((a, b) => b.totalVolume - a.totalVolume || a.name.localeCompare(b.name))
      .map((bu) => ({ ...bu, share: grandTotal > 0 ? (bu.totalVolume / grandTotal) * 100 : 0 }));
  }, [businessUnits, contractsData, search]);

  const isLoading = isLoadingBUs || isLoadingContracts;
  const columnCount = isAdmin ? 8 : 7;

  const confirmPendingAction = () => {
    if (!pendingAction) return;
    const mutation = pendingAction.type === 'delete' ? deleteBusinessUnit : restoreBusinessUnit;
    mutation.mutate(pendingAction.bu.id, { onSettled: () => setPendingAction(null) });
  };

  const openCreate = () => setDialog({ open: true, businessUnit: null });
  const openEdit = (bu: BusinessUnit) => setDialog({ open: true, businessUnit: bu });

  return (
    <Layout>
      <ScrollPage
        framed={!error && !isLoading && rows.length > 0}
        footer={
          !error && !isLoading && rows.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              Showing {rows.length} of {businessUnits?.length ?? 0} business {(businessUnits?.length ?? 0) === 1 ? 'unit' : 'units'}
            </p>
          ) : null
        }
        header={
          <>
      <PageHeader
        title="Business Units"
        description={
          isLoading
            ? 'Business units and the contract volume allocated to each'
            : `${businessUnits?.length ?? 0} business ${businessUnits?.length === 1 ? 'unit' : 'units'}, ranked by contract volume`
        }
        actions={
          isAdmin && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add business unit
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            aria-label="Search business units"
            placeholder="Search by name, code or location"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {search && (
          <Button variant="ghost" onClick={() => setSearch('')} className="text-muted-foreground">
            <X className="mr-1.5 h-4 w-4" />
            Clear search
          </Button>
        )}
        {isAdmin && (
          <div className="flex h-10 items-center gap-2 sm:ml-auto">
            <Switch id="show-deleted-bus" checked={showDeleted} onCheckedChange={setShowDeleted} />
            <Label htmlFor="show-deleted-bus" className="cursor-pointer text-sm font-normal">
              Show deleted
            </Label>
          </div>
        )}
      </div>

          </>
        }
      >
      {error ? (
        <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          Business units could not be loaded. Please try again later.
        </div>
      ) : isLoading ? (
        <Card className="overflow-hidden" aria-busy="true" aria-label="Loading business units">
          <div className="divide-y">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="flex items-center gap-6 px-6 py-4">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="hidden h-4 w-32 md:block" />
                <Skeleton className="ml-auto h-4 w-20" />
              </div>
            ))}
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-lg border border-dashed px-6 py-16 text-center">
          <Building2 className="h-9 w-9 text-muted-foreground/60" aria-hidden="true" />
          <p className="mt-4 text-base font-semibold">{search ? 'No business units match your search' : 'No business units yet'}</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {search
              ? 'Try a different name, code or location.'
              : 'Add the first business unit so volumes can be assigned to it on contracts.'}
          </p>
          {!search && isAdmin && (
            <Button className="mt-6" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add business unit
            </Button>
          )}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="w-10 pl-4">
                  <span className="sr-only">Expand</span>
                </TableHead>
                <TableHead className="min-w-[14rem]">Business unit</TableHead>
                <TableHead>Code</TableHead>
                <TableHead className="hidden xl:table-cell">Location</TableHead>
                <TableHead className="text-right">Contracts</TableHead>
                <TableHead className="text-right">Volume</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Share</TableHead>
                {isAdmin && (
                  <TableHead className="w-[120px] pr-6 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((bu) => {
                const isExpanded = expandedBuId === bu.id;
                return (
                  <Fragment key={bu.id}>
                    <TableRow
                      className={cn('cursor-pointer', !bu.isActive && 'text-muted-foreground')}
                      onClick={() => setExpandedBuId(isExpanded ? null : bu.id)}
                    >
                      <TableCell className="pl-4">
                        <button
                          type="button"
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Hide' : 'Show'} contracts for ${bu.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedBuId(isExpanded ? null : bu.id);
                          }}
                          className="rounded-sm p-1 text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <ChevronRight className={cn('h-4 w-4 transition-transform duration-150', isExpanded && 'rotate-90')} />
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{bu.name}</span>
                          {!bu.isActive && <Badge variant="destructive">Deleted</Badge>}
                        </div>
                        {bu.description && <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{bu.description}</p>}
                      </TableCell>
                      <TableCell>{bu.code}</TableCell>
                      <TableCell className="hidden xl:table-cell">{bu.location}</TableCell>
                      <TableCell className="text-right">{bu.contracts.length}</TableCell>
                      <TableCell className="whitespace-nowrap text-right font-medium">
                        {formatNumber(bu.totalVolume, 1)} <span className="font-normal text-muted-foreground">tons</span>
                      </TableCell>
                      <TableCell className="hidden text-right sm:table-cell">{formatNumber(bu.share, 1)}%</TableCell>
                      {isAdmin && (
                        <TableCell className="pr-6" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {bu.isActive ? (
                              <>
                                <RowAction label="Edit">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Edit ${bu.name}`} onClick={() => openEdit(bu)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </RowAction>
                                <RowAction label="Delete">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                    aria-label={`Delete ${bu.name}`}
                                    onClick={() => setPendingAction({ type: 'delete', bu })}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </RowAction>
                              </>
                            ) : (
                              <RowAction label="Restore">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label={`Restore ${bu.name}`}
                                  onClick={() => setPendingAction({ type: 'restore', bu })}
                                >
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              </RowAction>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columnCount} className="bg-muted/30 p-0">
                          {bu.contracts.length === 0 ? (
                            <p className="px-6 py-5 pl-14 text-sm text-muted-foreground">No contracts use {bu.name} yet.</p>
                          ) : (
                            <div className="px-4 py-3 sm:pl-14 sm:pr-6">
                              <Table>
                                <TableHeader>
                                  <TableRow className="hover:bg-transparent">
                                    <TableHead className="h-9">Contract</TableHead>
                                    <TableHead className="hidden h-9 sm:table-cell">Supplier</TableHead>
                                    <TableHead className="hidden h-9 md:table-cell">Contract date</TableHead>
                                    <TableHead className="h-9 text-right">Contract volume</TableHead>
                                    <TableHead className="h-9 text-right">Allocated</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {bu.contracts.map((contract) => {
                                    const tons = allocatedTons(contract, bu.name);
                                    const pct = contract.totalVolume > 0 ? ((tons * 1000) / contract.totalVolume) * 100 : 0;
                                    return (
                                      <TableRow
                                        key={contract.id}
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/contracts/${contract.id}`);
                                        }}
                                      >
                                        <TableCell className="py-2.5 font-medium">
                                          {contract.contractName}
                                          <span className="block text-xs font-normal text-muted-foreground">{contract.productName}</span>
                                        </TableCell>
                                        <TableCell className="hidden py-2.5 sm:table-cell">{contract.supplierName}</TableCell>
                                        <TableCell className="hidden whitespace-nowrap py-2.5 md:table-cell">
                                          {formatCalendarDate(contract.contractDate)}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap py-2.5 text-right">
                                          {formatNumber(contract.totalVolume / 1000, 1)} tons
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap py-2.5 text-right">
                                          {formatNumber(tons, 1)} tons <span className="text-muted-foreground">({formatNumber(pct, 0)}%)</span>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
      </ScrollPage>

      <BusinessUnitDialog
        open={dialog.open}
        businessUnit={dialog.businessUnit}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
      />

      <ConfirmActionDialog
        open={!!pendingAction}
        destructive={pendingAction?.type === 'delete'}
        isPending={deleteBusinessUnit.isPending || restoreBusinessUnit.isPending}
        title={pendingAction?.type === 'delete' ? 'Delete business unit?' : 'Restore business unit?'}
        description={
          pendingAction?.type === 'delete'
            ? `"${pendingAction.bu.name}" will be hidden from business unit lists. Deletion is blocked while active contracts use it. You can restore it later.`
            : `"${pendingAction?.bu.name}" will become available again.`
        }
        confirmLabel={pendingAction?.type === 'delete' ? 'Delete' : 'Restore'}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />
    </Layout>
  );
}
